// Restricted (c) Siemens Healthineers AG, 2025, All rights reserved.
import { AsyncPipe, NgTemplateOutlet } from '@angular/common';
import {
  CUSTOM_ELEMENTS_SCHEMA,
  Component,
  Input,
  OnChanges,
  inject,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import moment from 'moment';
import { map } from 'rxjs';
import { ClipboardService } from 'src/app/modules/shared/services/clipboard.service';
import {
  ActivityStatus,
  OperationalStateColor,
} from '../../../../_interfaces/service-ticket-model';
import { APICallGateComponent } from '../../../shared/components/api-call-gate.component';
import {
  LocalizedDatePipe,
  LocalizedTimePipe,
} from '../../../shared/pipes/localizedDate.pipe';
import { ScreenLayoutService } from '../../../shared/services/screen-layout-service/screen-layout.service';
import { DeepReadonly } from '../../../shared/utils/api-call-state.util';
import {
  ServiceTicketsService,
  TicketModelFIXED,
} from '../../services/service-tickets.service';
import { isValidTicket } from '../service-tickets-page/service-tickets.shared';
import { ApplicationInsightsService } from '../../services/application-insights/application-insights-service/application-insights.service';

const today = moment().endOf('day');
@Component({
  selector: 'app-planned-activities-panel',
  templateUrl: './planned-activities-panel.component.html',
  styleUrls: [
    '../service-tickets-page/service-tickets-shared.scss',
    './planned-activities-panel.component.scss',
  ],
  standalone: true,
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  imports: [
    APICallGateComponent,
    NgTemplateOutlet,
    LocalizedDatePipe,
    LocalizedTimePipe,
    AsyncPipe,
    TranslateModule,
  ],
})
export class PlannedActivitiesPanelComponent implements OnChanges {
  protected router = inject(Router);
  protected route = inject(ActivatedRoute);
  protected serviceTicketsService = inject(ServiceTicketsService);
  protected screenLayoutService = inject(ScreenLayoutService);
  protected clipboard = inject(ClipboardService);
  private applicationInsightsService = inject(ApplicationInsightsService);
  @Input({ required: true }) systemMaterialNumber!: number;
  @Input({ required: true }) systemSerialNumber!: string;
  protected plannedActivities$ =
    // serviceTicketsService, even though named as such, provides all tickets
    this.serviceTicketsService.getTickets.lastSuccessfulData$.pipe(
      // filter for valid tickets
      map((tickets) => {
        return tickets.filter(isValidTicket);
      }),
    );
  nextPlannedActivity$ = this.plannedActivities$.pipe(
    // get only future tickets
    map((tickets) =>
      tickets.filter((ticket) => moment(ticket.plannedStart).isAfter(today)),
    ),
    // sort future tickets
    map((tickets) =>
      [...tickets].sort((a, b) => {
        return moment(a.plannedStart).diff(moment(b.plannedStart), 'minutes');
      }),
    ),
    // compute status color
    map((tickets) => {
      return tickets.slice(0, 1).map((ticket) => ({
        ...ticket,
        statusColor: statusColor(ticket),
      }));
    }),
  );
  lastOverdueActivity$ = this.plannedActivities$.pipe(
      // get only overdue tickets (including ServiceTickets with no null start/end dates)
      map((tickets) =>
        tickets.filter(
          (ticket) =>
            ticket.plannedEnd && moment(ticket.plannedEnd).isBefore(today),
        ),
      ),
      // sort overdue tickets by planned end date (most recent first)
      map((tickets) =>
        [...tickets].sort((a, b) => {
          return moment(b.plannedEnd).diff(moment(a.plannedEnd), 'minutes');
        }),
      ),
      // compute status color
      map((tickets) => {
        return tickets.slice(0, 1).map((ticket) => ({
          ...ticket,
          statusColor: statusColor(ticket),
        }));
      }),
    );
  ticketCountDisplay$ = this.plannedActivities$.pipe(
      map((tickets) => {
        return tickets.length ? `(${tickets.length})` : '-';
      }),
    );
  ngOnChanges(): void {
    // got to be system props
    this.serviceTicketsService.getTickets.request({
      systemMaterialNumber: this.systemMaterialNumber,
      systemSerialNumber: this.systemSerialNumber,
    });
  }
  isOverflowing(el: HTMLElement): boolean {
    if (!el.style.overflow || el.style.overflow === 'visible') {
      el.style.overflow = 'hidden';
    }
    return el.clientWidth < el.scrollWidth || el.clientHeight < el.scrollHeight;
  }
  loadPlannedView = (): void => {
    this.applicationInsightsService.trackComponentUsageEvent(
      'PlannedActivitiesOpened',
      {
        activityType: ActivityStatus.Scheduled,
      },
    );
    this.router.navigate(['activities'], {
      queryParams: { type: ActivityStatus.Scheduled },
      relativeTo: this.route,
    });
  };
  loadOverdueView = (): void => {
    this.applicationInsightsService.trackComponentUsageEvent(
      'OverdueActivitiesOpened',
      {
        activityType: ActivityStatus.Overdue,
      },
    );
    this.router.navigate(['activities'], {
      queryParams: { type: ActivityStatus.Overdue },
      relativeTo: this.route,
    });
  };
}
export function statusColor(ticket: DeepReadonly<TicketModelFIXED>): string {
  if (!ticket.plannedStart) {
    return OperationalStateColor.Yellow;
  }
  if (moment(ticket.plannedStart).isAfter(today)) {
    return OperationalStateColor.Green;
  }
  if (moment(ticket.plannedEnd).isBefore(today)) {
    return OperationalStateColor.Red;
  }
  return OperationalStateColor.Red;
}
